import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EpisodePicker } from '@/components/player/EpisodePicker';
import { PosterImage } from '@/components/ui/PosterImage';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/StateMessage';
import { useAnimeDetail } from '@/hooks/useAnimeQueries';
import { useLibrary } from '@/hooks/useLibrary';
import {
	formatPeople,
	formatReleaseStatus,
	formatScore,
	formatViews,
} from '@/lib/format';

export function AnimeDetailPage() {
	const { slug } = useParams<{ slug: string }>();
	const detailQuery = useAnimeDetail(slug);
	const { isSaved, toggle, recordHistory } = useLibrary();
	const [serverIndex, setServerIndex] = useState(0);

	const anime = detailQuery.data;

	// Ghi vào "Lịch sử xem" ngay khi mở trang chi tiết, như bản gốc làm lúc
	// mở modal — nhờ vậy thẻ "Đang xem tiếp" ở sidebar luôn có dữ liệu.
	useEffect(() => {
		if (anime) recordHistory(anime);
	}, [anime, recordHistory]);

	useEffect(() => {
		if (anime) document.title = `${anime.title} — NekoStream`;
		return () => {
			document.title = 'NekoStream — Anime Hub';
		};
	}, [anime]);

	if (detailQuery.isPending) {
		return (
			<div className="mt-6 grid gap-6 min-[700px]:grid-cols-[260px_1fr]">
				<Skeleton className="aspect-[2/2.8] w-full" />
				<div className="grid gap-3">
					<Skeleton className="h-9 w-2/3" />
					<Skeleton className="h-4 w-1/3" />
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-11 w-56" />
				</div>
			</div>
		);
	}

	if (detailQuery.error || !anime) {
		return (
			<div className="mt-10">
				<ErrorState
					error={detailQuery.error ?? new Error('Không tìm thấy phim.')}
					onRetry={() => void detailQuery.refetch()}
				/>
			</div>
		);
	}

	const firstEpisode = anime.servers[0]?.episodes[0];
	const favorited = isSaved('favorites', anime.slug);
	const inWatchlist = isSaved('watchlist', anime.slug);

	const infoRows: Array<[string, string]> = [
		['Trạng thái', formatReleaseStatus(anime.releaseStatus)],
		[
			'Số tập',
			anime.totalEpisodes
				? `${anime.episodeCurrent || '—'} / ${anime.totalEpisodes} tập`
				: anime.episodeCurrent || 'Đang cập nhật',
		],
		['Thời lượng', anime.durationLabel || 'Đang cập nhật'],
		['Ngôn ngữ', anime.lang || 'Đang cập nhật'],
		['Chất lượng', anime.quality || 'Đang cập nhật'],
		['Lượt xem', formatViews(anime.views)],
		[
			'Quốc gia',
			anime.countries.map((country) => country.name).join(', ') ||
				'Đang cập nhật',
		],
		['Diễn viên', formatPeople(anime.actors)],
		['Đạo diễn', formatPeople(anime.directors)],
	];

	return (
		<>
			<article className="relative mt-6 overflow-hidden rounded-hero border border-white/[0.06] bg-surface/60 p-5 shadow-neko min-[700px]:p-7">
				<img
					src={anime.thumbUrl}
					alt=""
					aria-hidden="true"
					className="absolute inset-0 -z-20 size-full object-cover opacity-25 blur-xl"
				/>
				<span
					className="absolute inset-0 -z-10 bg-linear-to-t from-bg via-bg/80 to-bg/40"
					aria-hidden="true"
				/>

				<div className="grid gap-6 min-[700px]:grid-cols-[240px_1fr]">
					<PosterImage
						src={anime.posterUrl}
						alt={`Poster ${anime.title}`}
						eager
						className="mx-auto w-[190px] rounded-card border border-white/[0.06] object-cover shadow-neko min-[700px]:w-full"
					/>

					<div className="min-w-0">
						<h1 className="text-[clamp(24px,3.2vw,38px)] leading-tight font-extrabold tracking-[-1px]">
							{anime.title}
						</h1>
						{anime.originalTitle && (
							<p className="mt-1 text-sm text-muted">{anime.originalTitle}</p>
						)}

						<div className="mt-3 flex flex-wrap items-center gap-2 text-[13px]">
							{anime.quality && (
								<span className="rounded-[5px] bg-accent/20 px-2 py-0.5 text-[#ffd4e8]">
									{anime.quality}
								</span>
							)}
							{anime.score !== null && (
								<span className="rounded-md bg-[#191522dd] px-2 py-0.5 font-bold text-gold">
									★ {formatScore(anime.score)}
								</span>
							)}
							{anime.year && <span className="text-[#c3c4d4]">{anime.year}</span>}
							{anime.episodeCurrent && (
								<span className="text-[#c3c4d4]">{anime.episodeCurrent}</span>
							)}
							{anime.lang && <span className="text-[#c3c4d4]">{anime.lang}</span>}
						</div>

						<p className="mt-4 max-w-[70ch] text-[#c6c4d4]">
							{anime.synopsis || 'Phim này chưa có mô tả nội dung.'}
						</p>

						{anime.genres.length > 0 && (
							<div className="mt-4 flex flex-wrap gap-2">
								{anime.genres.map((genre) => (
									<Link
										key={genre.slug}
										to={`/duyet?category=${genre.slug}`}
										className="rounded-full border border-line bg-white/[0.02] px-3 py-1.5 text-xs text-muted transition hover:border-accent hover:text-ink"
									>
										{genre.name}
									</Link>
								))}
							</div>
						)}

						<div className="mt-5 flex flex-wrap gap-2">
							{firstEpisode ? (
								<Link
									to={`/xem/${anime.slug}/${firstEpisode.slug}`}
									className="rounded-lg bg-linear-to-r from-accent to-accent-soft px-4.5 py-2.5 font-bold shadow-accent transition hover:-translate-y-0.5 active:scale-[0.97]"
								>
									▶ Xem {firstEpisode.name}
								</Link>
							) : (
								<span className="rounded-lg bg-white/5 px-4.5 py-2.5 font-bold text-muted">
									Chưa có link phát
								</span>
							)}

							<button
								type="button"
								onClick={() => toggle('favorites', anime)}
								className="rounded-lg bg-white/10 px-4.5 py-2.5 font-bold transition hover:-translate-y-0.5 active:scale-[0.97]"
							>
								{favorited ? '♥ Đã thích' : '♡ Yêu thích'}
							</button>

							<button
								type="button"
								onClick={() => toggle('watchlist', anime)}
								className="rounded-lg bg-white/10 px-4.5 py-2.5 font-bold transition hover:-translate-y-0.5 active:scale-[0.97]"
							>
								{inWatchlist ? '✓ Đã lưu' : '＋ Danh sách'}
							</button>

							{anime.trailerUrl && (
								<a
									href={anime.trailerUrl}
									target="_blank"
									rel="noreferrer noopener"
									className="rounded-lg px-4.5 py-2.5 font-bold text-muted transition hover:text-ink"
								>
									Trailer ↗
								</a>
							)}
						</div>
					</div>
				</div>
			</article>

			<section className="mt-10">
				<SectionHeader title="Danh sách tập" />
				<EpisodePicker
					servers={anime.servers}
					activeServerIndex={serverIndex}
					onServerChange={setServerIndex}
					activeEpisodeSlug={null}
					buildEpisodeHref={(episodeSlug) =>
						`/xem/${anime.slug}/${episodeSlug}`
					}
				/>
			</section>

			<section className="mt-10">
				<SectionHeader title="Thông tin phim" />
				<dl className="grid gap-x-6 gap-y-3 rounded-panel border border-line bg-surface/60 p-4.5 min-[700px]:grid-cols-2">
					{infoRows.map(([label, value]) => (
						<div key={label} className="flex min-w-0 gap-2 text-sm">
							<dt className="w-28 shrink-0 text-muted">{label}</dt>
							<dd className="min-w-0 flex-1 break-words">{value}</dd>
						</div>
					))}
				</dl>
			</section>
		</>
	);
}
